import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import Header from '../Header';
import {
    Store, Sparkles, Package, Users, ThumbsUp, MessageSquare,
    RotateCcw, FileDown, TrendingUp, LayoutDashboard, CalendarRange,
    AlertCircle, Loader2, ClipboardX, Search, ChevronLeft, ChevronRight,
    Download, Calendar, Filter, ChevronDown, ArrowUpRight, ArrowDownRight,
    X, Check
} from 'lucide-react';

// ─── Config ───────────────────────────────────────────────
const API_URL = 'https://tpapi.abisaio.com:3060/api/v1/reportsurveyresults?surveyId=1&AreaId=ALL&BranchId=ALL';

const RATING_ORDER = ['Excellent', 'Good', 'Average', 'POOR', 'Very Poor'];
const RECOMMEND_ORDER = ['Definitely Yes', 'Probably Yes', 'Probably not', 'Definitely not'];

const RATING_COLORS = {
    'Excellent': { bg: '#f0fdf4', border: '#bcf2d1', text: '#166534', bar: '#22c55e' },
    'Good': { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', bar: '#3b82f6' },
    'Average': { bg: '#fffbeb', border: '#fef3c7', text: '#92400e', bar: '#f59e0b' },
    'POOR': { bg: '#fff1f2', border: '#fecdd3', text: '#9f1239', bar: '#f43f5e' },
    'Very Poor': { bg: '#fef2f2', border: '#fee2e2', text: '#b91c1c', bar: '#ef4444' },
    'Definitely Yes': { bg: '#f0fdf4', border: '#bcf2d1', text: '#166534', bar: '#22c55e' },
    'Probably Yes': { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', bar: '#3b82f6' },
    'Probably not': { bg: '#fffbeb', border: '#fef3c7', text: '#92400e', bar: '#f59e0b' },
    'Definitely not': { bg: '#fff1f2', border: '#fecdd3', text: '#9f1239', bar: '#f43f5e' },
};

const CATEGORIES = [
    { key: 'AMBIENCE', label: 'Ambience', icon: <Store size={18} /> },
    { key: 'CLEANLY', label: 'Cleanliness', icon: <Sparkles size={18} /> },
    { key: 'AVAILABLE', label: 'Availability', icon: <Package size={18} /> },
    { key: 'STAFF', label: 'Staff', icon: <Users size={18} /> },
    { key: 'RECOMMEND', label: 'Recommend', icon: <ThumbsUp size={18} /> },
];

// NPS Score: (Promoters - Detractors) / Total Response * 100
// Promoters: 5 & 4 stars (Definitely Yes / Probably Yes / Excellent / Good)
// Detractors: 2 & 1 stars (Probably not / Definitely not / POOR / Very Poor)
const calcNPS = (data, cat = 'RECOMMEND') => {
    if (!data.length) return 0;
    const promoters = data.filter(r => r[cat] === 'Excellent' || r[cat] === 'Good' || r[cat] === 'Definitely Yes' || r[cat] === 'Probably Yes').length;
    const detractors = data.filter(r => r[cat] === 'Probably not' || r[cat] === 'Definitely not' || r[cat] === 'POOR' || r[cat] === 'Very Poor').length;
    return Math.round(((promoters - detractors) / data.length) * 100);
};

const getNPSColor = (score) => {
    if (score >= 50) return { text: '#166534', bg: '#f0fdf4', border: '#bcf2d1', label: 'Excellent' };
    if (score >= 0) return { text: '#1e40af', bg: '#eff6ff', border: '#bfdbfe', label: 'Good' };
    return { text: '#991b1b', bg: '#fef2f2', border: '#fecdd3', label: 'Needs Work' };
};

// ─── Date Helpers ─────────────────────────────────────────
const toYMD = (d) => d.toISOString().slice(0, 10);

const getPeriodDates = (period) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(today); end.setHours(23, 59, 59, 999);

    if (period === 'today') {
        return { from: toYMD(today), to: toYMD(today) };
    }
    if (period === 'weekly') {
        const from = new Date(today);
        from.setDate(today.getDate() - 6);
        return { from: toYMD(from), to: toYMD(today) };
    }
    if (period === 'monthly') {
        const from = new Date(today);
        from.setDate(today.getDate() - 29);
        return { from: toYMD(from), to: toYMD(today) };
    }
    if (period === 'yearly') {
        const from = new Date(today);
        from.setFullYear(today.getFullYear() - 1);
        return { from: toYMD(from), to: toYMD(today) };
    }
    return { from: '', to: '' };
};

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} `
        + `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// ─── Rating Badge ─────────────────────────────────────────
const RatingBadge = ({ value }) => {
    const cfg = RATING_COLORS[value] || { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
            fontSize: '10px', fontWeight: '800', backgroundColor: cfg.bg, color: cfg.text,
            border: `1px solid ${cfg.border || 'transparent'}`,
            letterSpacing: '0.4px', whiteSpace: 'nowrap', textTransform: 'uppercase'
        }}>
            {value || '-'}
        </span>
    );
};

// ─── NPS Gauge Arc ────────────────────────────────────────
const NPSGauge = ({ score }) => {
    const npsColor = getNPSColor(score);
    const clamp = Math.max(-100, Math.min(100, score));
    const pct = (clamp + 100) / 200;
    const radius = 38, cx = 60, cy = 46;
    const circumference = Math.PI * radius;
    const dashOffset = circumference * (1 - pct);
    const scoreColor = score >= 50 ? '#10b981' : score >= 0 ? '#3b82f6' : '#f43f5e';
    const TrendIcon = score >= 50 ? ArrowUpRight : score >= 0 ? TrendingUp : ArrowDownRight;

    return (
        <div style={{ textAlign: 'center', position: 'relative', padding: '4px 0 0 0' }}>
            <svg width="120" height="54" viewBox="0 0 120 54" style={{ overflow: 'visible' }}>
                <path d={`M ${cx - radius},${cy} A ${radius},${radius} 0 0,1 ${cx + radius},${cy}`}
                    fill="none" stroke="#f1f5f9" strokeWidth="6" strokeLinecap="round" />
                <path d={`M ${cx - radius},${cy} A ${radius},${radius} 0 0,1 ${cx + radius},${cy}`}
                    fill="none" stroke={scoreColor} strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={circumference} strokeDashoffset={dashOffset}
                    style={{ transition: 'stroke-dashoffset 0.8s ease', opacity: 0.9 }}
                />
            </svg>
            <div style={{ position: 'absolute', bottom: '16px', left: 0, right: 0 }}>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                    {score}
                    <TrendIcon size={14} style={{ color: scoreColor, opacity: 0.8 }} />
                </div>
                <div style={{ fontSize: '8px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: '-2px' }}>NPS Score</div>
            </div>
            <div style={{ marginTop: '4px' }}>
                <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
                    fontSize: '9px', fontWeight: '800', border: `1px solid ${npsColor.border}`,
                    backgroundColor: npsColor.bg, color: npsColor.text, textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    {npsColor.label}
                </span>
            </div>
        </div>
    );
};

// ─── Rating Breakdown Card ─────────────────────────────────
const RatingBreakdown = ({ data, categoryKey, label, icon }) => {
    const order = categoryKey === 'RECOMMEND' ? RECOMMEND_ORDER : RATING_ORDER;
    const counts = {};
    data.forEach(r => { const v = r[categoryKey] || 'Unknown'; counts[v] = (counts[v] || 0) + 1; });
    const total = data.length || 1;
    const topRating = order.find(r => counts[r] > 0) || order[0];
    const topPct = Math.round(((counts[topRating] || 0) / total) * 100);

    return (
        <div style={{
            background: '#fff', borderRadius: '12px', padding: '14px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9',
            transition: 'transform 0.2s, box-shadow 0.2s',
            minWidth: '220px', flex: '1 0 auto'
        }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)'; }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <div style={{ color: '#475569', background: '#f8fafc', padding: '4px', borderRadius: '6px' }}>{icon}</div>
                <div>
                    <div style={{ fontSize: '12px', fontWeight: '800', color: '#1e293b' }}>{label}</div>
                    <div style={{ fontSize: '9px', color: '#94a3b8' }}>{data.length} responses</div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>{topPct}%</div>
                    <div style={{ fontSize: '8px', color: '#94a3b8' }}>top rating</div>
                </div>
            </div>
            {order.map(rating => {
                const count = counts[rating] || 0;
                const pct = Math.round((count / total) * 100);
                const cfg = RATING_COLORS[rating] || { bar: '#9ca3af', bg: '#f3f4f6', text: '#374151' };
                return (
                    <div key={rating} style={{ marginBottom: '5px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '2px' }}>
                            <span style={{ color: '#475569', fontWeight: '600' }}>{rating}</span>
                            <span style={{ color: '#94a3b8', fontWeight: '500' }}>{count} <span style={{ color: cfg.text, fontWeight: '700' }}>({pct}%)</span></span>
                        </div>
                        <div style={{ height: '4px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%', width: `${pct}%`, background: cfg.bar,
                                borderRadius: '4px', transition: 'width 0.6s ease'
                            }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Period Tab Bar ────────────────────────────────────────
const TABS = [
    { id: 'today', label: 'Today' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'yearly', label: 'Yearly' },
    { id: 'custom', label: 'Custom' },
];

// ─── Main Dashboard ───────────────────────────────────────
const FeedbackDashboard = () => {
    const [allData, setAllData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [filterCity, setFilterCity] = useState('ALL');
    const [filterArea, setFilterArea] = useState('ALL');
    const [filterBranch, setFilterBranch] = useState('ALL');
    const [stagedFilters, setStagedFilters] = useState({ city: 'ALL', area: 'ALL', branch: 'ALL' });
    const [showFilterPopover, setShowFilterPopover] = useState(false);
    const [showDatePopover, setShowDatePopover] = useState(false);
    const [activePeriod, setActivePeriod] = useState('monthly');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const PAGE_SIZE = 15;

    // Compute active date range
    const dateRange = useMemo(() => {
        if (activePeriod === 'custom') return { from: customFrom, to: customTo };
        return getPeriodDates(activePeriod);
    }, [activePeriod, customFrom, customTo]);

    // Fetch
    const fetchData = async () => {
        setLoading(true); setError('');
        try {
            const token = localStorage.getItem('AuthorizationToken');
            const res = await fetch(API_URL, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            if (res.status === 401 || res.status === 403) {
                window.alert('⚠️ Session expired. Please login again.');
                setError('Unauthorized. Please login again.');
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setAllData(Array.isArray(json) ? json : []);
        } catch (e) {
            setError(`Failed to load data: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // Filter options
    const cities = useMemo(() => ['ALL', ...new Set(allData.map(r => r.CityName || r.City).filter(Boolean))].sort(), [allData]);
    const areas = useMemo(() => ['ALL', ...new Set(allData.map(r => r.AreaName).filter(Boolean))].sort(), [allData]);
    const branches = useMemo(() => ['ALL', ...new Set(allData.map(r => r.BranchName).filter(Boolean))].sort(), [allData]);

    // Date-filtered data
    const dateFiltered = useMemo(() => {
        return allData.filter(r => {
            const rowDate = r.BusinessDate ? r.BusinessDate.slice(0, 10) : '';
            if (dateRange.from && rowDate < dateRange.from) return false;
            if (dateRange.to && rowDate > dateRange.to) return false;
            if (filterCity !== 'ALL' && (r.CityName || r.City) !== filterCity) return false;
            if (filterArea !== 'ALL' && r.AreaName !== filterArea) return false;
            if (filterBranch !== 'ALL' && r.BranchName !== filterBranch) return false;
            return true;
        });
    }, [allData, dateRange, filterCity, filterArea, filterBranch]);

    // Search-filtered data
    const filtered = useMemo(() => {
        if (!search) return dateFiltered;
        const q = search.toLowerCase();
        return dateFiltered.filter(r =>
            (r.UserName || '').toLowerCase().includes(q) ||
            (r.RefDocID || '').toLowerCase().includes(q) ||
            (r.BillNo || '').toLowerCase().includes(q) ||
            (r.Mobile || '').toLowerCase().includes(q) ||
            (r.SaleItems || '').toLowerCase().includes(q)
        );
    }, [dateFiltered, search]);

    // KPIs
    const totalResp = filtered.length;
    const npsScore = calcNPS(filtered);
    const getCatNPS = (cat) => calcNPS(filtered, cat);

    // Pagination
    useEffect(() => setPage(1), [activePeriod, customFrom, customTo, search, filterCity, filterArea, filterBranch]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Excel Export
    const exportToExcel = () => {
        if (!filtered.length) return;
        const wb = XLSX.utils.book_new();
        const headers = ['Unique ID', 'Bill No', 'Ref Doc ID', 'Business Date', 'Created Date', 'City', 'Area', 'Branch', 'User Name', 'Mobile', 'Sale Items', 'Ambience', 'Cleanliness', 'Availability', 'Staff', 'Recommend'];
        const rows = filtered.map(r => [
            r.UniqueID || '-', r.BillNo || '-', r.RefDocID || '-', formatDate(r.BusinessDate), formatDateTime(r.CreatedDate),
            r.CityName || r.City || '-', r.AreaName || '-', r.BranchName || '-', r.UserName || '-', r.Mobile || '-', r.SaleItems || '-',
            r.AMBIENCE || '-', r.CLEANLY || '-', r.AVAILABLE || '-', r.STAFF || '-', r.RECOMMEND || '-',
        ]);
        const ws1 = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        ws1['!cols'] = [{ wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 13 }, { wch: 30 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws1, 'Responses');

        const branchSummary = {};
        filtered.forEach(r => {
            const b = r.BranchName || 'Unknown';
            if (!branchSummary[b]) branchSummary[b] = { city: r.CityName || r.City || '-', total: 0, excellent: 0, recommend: 0, promoters: 0, detractors: 0 };
            branchSummary[b].total++;
            if (r.AMBIENCE === 'Excellent' && r.CLEANLY === 'Excellent' && r.AVAILABLE === 'Excellent' && r.STAFF === 'Excellent') branchSummary[b].excellent++;
            if (r.RECOMMEND === 'Definitely Yes' || r.RECOMMEND === 'Probably Yes') branchSummary[b].recommend++;
            if (r.RECOMMEND === 'Definitely Yes' || r.RECOMMEND === 'Probably Yes' || r.RECOMMEND === 'Excellent' || r.RECOMMEND === 'Good') branchSummary[b].promoters++;
            if (r.RECOMMEND === 'Probably not' || r.RECOMMEND === 'Definitely not' || r.RECOMMEND === 'POOR' || r.RECOMMEND === 'Very Poor') branchSummary[b].detractors++;
        });
        const sh = ['City', 'Branch', 'Total Responses', 'All Excellent', 'Excellent %', 'Recommend', 'Recommend %', 'NPS Score'];
        const sr = Object.entries(branchSummary).map(([b, s]) => [
            s.city, b, s.total, s.excellent, `${Math.round(s.excellent / s.total * 100)}%`,
            s.recommend, `${Math.round(s.recommend / s.total * 100)}%`,
            Math.round(((s.promoters - s.detractors) / s.total) * 100),
        ]);
        const ws2 = XLSX.utils.aoa_to_sheet([sh, ...sr]);
        ws2['!cols'] = [{ wch: 18 }, { wch: 24 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Branch Summary');
        XLSX.writeFile(wb, `Feedback_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // ── Period label ──
    const periodLabel = useMemo(() => {
        if (activePeriod === 'custom') {
            if (customFrom && customTo) return `${formatDate(customFrom)} – ${formatDate(customTo)}`;
            return 'Custom range';
        }
        const dr = getPeriodDates(activePeriod);
        return `${formatDate(dr.from)} – ${formatDate(dr.to)}`;
    }, [activePeriod, customFrom, customTo]);

    // ── Styles ──
    const s = {
        page: { minHeight: '100vh', background: '#f0f4f8', fontFamily: "'Segoe UI',Inter,sans-serif" },
        body: { padding: '16px 20px', maxWidth: '1700px', margin: '0 auto' },
        // Header bar
        headerBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' },
        titleWrap: {},
        title: { fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.3px' },
        subtitle: { fontSize: '12px', color: '#64748b', marginTop: '2px' },
        // Tab bar / Global Filters
        tabBar: { display: 'flex', alignItems: 'center', background: '#fff', borderRadius: '12px', padding: '12px 18px', boxShadow: '0 4px 15px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' },
        tabInner: { display: 'flex', gap: '4px', flexWrap: 'wrap', background: '#f1f5f9', padding: '4px', borderRadius: '10px', border: '1px solid #e2e8f0' },
        globalFilters: { display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' },
        hSelectGroup: { display: 'flex', flexDirection: 'column', gap: '2px' },
        hSelectLabel: { fontSize: '9px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' },
        hSelect: { padding: '4px 8px', fontSize: '12px', fontWeight: '700', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', background: '#f8fafc', color: '#1e293b', cursor: 'pointer', minWidth: '120px' },
        kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(145px,1fr))', gap: '12px', marginBottom: '20px' },
        kpiCard: { borderRadius: '12px', padding: '12px 14px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)', transition: 'transform 0.2s', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '8px' },
        kpiNum: { fontSize: '22px', fontWeight: '800', lineHeight: 1.1, color: '#1e293b' },
        kpiLbl: { fontSize: '11px', color: '#64748b', fontWeight: '700' },
        // Chart row
        chartRow: { display: 'flex', overflowX: 'auto', gap: '16px', paddingBottom: '12px', marginBottom: '14px', scrollbarWidth: 'none', '-ms-overflow-style': 'none' },
        // Filters
        filters: { background: '#fff', borderRadius: '14px', padding: '14px 16px', marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' },
        label: { fontSize: '11px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' },
        input: { padding: '8px 12px', fontSize: '12px', border: '1.5px solid #e2e8f0', borderRadius: '8px', outline: 'none', background: '#f8fafc', color: '#1e293b', transition: 'border-color 0.2s' },
        select: { padding: '8px 12px', fontSize: '12px', border: '1.5px solid #e2e8f0', borderRadius: '8px', outline: 'none', background: '#f8fafc', color: '#1e293b', transition: 'border-color 0.2s', cursor: 'pointer' },
        // Table
        tbl: { background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', marginBottom: '12px' },
        th: { padding: '14px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textAlign: 'left', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', letterSpacing: '0.6px', textTransform: 'uppercase' },
        td: { padding: '12px 16px', fontSize: '12px', color: '#1e293b', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' },
        // Buttons
        btnBlue: { padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 1px 2px rgba(37,99,235,0.2)', transition: 'all 0.2s' },
        btnGray: { padding: '8px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s' },
    };

    const KPI_CARDS = [
        { num: totalResp, lbl: 'Total Reps.', color: '#1e3a8a', icon: <MessageSquare size={16} />, bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '#bfdbfe' },
        { num: getCatNPS('AMBIENCE'), lbl: 'Ambience', color: '#064e3b', icon: <Store size={16} />, bg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '#a7f3d0' },
        { num: getCatNPS('CLEANLY'), lbl: 'Cleanliness', color: '#0c4a6e', icon: <Sparkles size={16} />, bg: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '#bae6fd' },
        { num: getCatNPS('AVAILABLE'), lbl: 'Availability', color: '#78350f', icon: <Package size={16} />, bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '#fde68a' },
        { num: getCatNPS('STAFF'), lbl: 'Staff Score', color: '#312e81', icon: <Users size={16} />, bg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '#c7d2fe' },
        { num: getCatNPS('RECOMMEND'), lbl: 'Recommend', color: '#4c1d95', icon: <ThumbsUp size={16} />, bg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', border: '#ddd6fe' },
    ];

    return (
        <>
            {/* Inject font */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; }
                input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
                .kpi-card:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(0,0,0,0.10) !important; }
            `}</style>
            <div style={s.page}>
                <Header />
                <div style={s.body}>

                    {/* ── Page Header ── */}
                    <div style={{ ...s.headerBar, marginBottom: '24px' }}>
                        <div style={s.titleWrap}>
                            <h2 style={{ ...s.title, display: 'flex', alignItems: 'center', gap: '12px', fontSize: '24px' }}>
                                <LayoutDashboard size={28} style={{ color: '#3b82f6' }} />
                                Customer Feedback Dashboard
                            </h2>
                            <p style={{ ...s.subtitle, fontSize: '13px', marginLeft: '40px' }}>Net Promoter Score &amp; Survey Analytics · <strong style={{ color: '#3b82f6' }}>{periodLabel}</strong></p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={{ ...s.btnGray, padding: '10px 20px' }} onClick={fetchData} disabled={loading}>
                                <RotateCcw size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                {loading ? 'Fetching…' : 'Refresh'}
                            </button>
                            <button style={{ ...s.btnBlue, padding: '10px 20px' }} onClick={exportToExcel} disabled={!filtered.length}>
                                <FileDown size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                Export Excel
                            </button>
                        </div>
                    </div>

                    {/* ── Global Filters & Tabs ── */}
                    <div style={s.tabBar}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                            <div style={s.tabInner}>
                                {TABS.map(tab => {
                                    const active = activePeriod === tab.id;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActivePeriod(tab.id)}
                                            style={{
                                                padding: '6px 18px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                fontSize: '13px',
                                                fontWeight: active ? '600' : '500',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                background: active ? '#fff' : 'transparent',
                                                color: active ? '#0f172a' : '#64748b',
                                                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                            }}
                                        >
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Intimate Custom date pickers dropdown next to tabs */}
                            {activePeriod === 'custom' && (
                                <div style={{ position: 'relative', marginLeft: '-4px' }}>
                                    <button
                                        onClick={() => setShowDatePopover(!showDatePopover)}
                                        style={{ ...s.btnGray, padding: '5px 12px', display: 'flex', alignItems: 'center', gap: '6px', background: showDatePopover ? '#eff6ff' : '#fff', borderColor: showDatePopover ? '#3b82f6' : '#e2e8f0', color: showDatePopover ? '#2563eb' : '#475569', borderRadius: '6px' }}
                                    >
                                        <Calendar size={14} />
                                        <span style={{ fontSize: '12px', fontWeight: '700' }}>
                                            {customFrom && customTo ? `${formatDate(customFrom)} - ${formatDate(customTo)}` : 'Select Dates'}
                                        </span>
                                        <ChevronDown size={14} style={{ transform: showDatePopover ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                    </button>

                                    {showDatePopover && (
                                        <div style={{
                                            position: 'absolute', top: '120%', left: 0,
                                            background: '#fff', borderRadius: '12px', padding: '16px',
                                            boxShadow: '0 15px 40px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0',
                                            zIndex: 100, minWidth: '260px', display: 'flex', flexDirection: 'column', gap: '16px'
                                        }}>
                                            <div style={{ fontSize: '12px', fontWeight: '800', color: '#1e293b', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                                Custom Range
                                                <button onClick={() => setShowDatePopover(false)} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={16} /></button>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                <div>
                                                    <label style={{ ...s.label, fontSize: '10px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '700' }}>START DATE</label>
                                                    <input type="date" style={{ ...s.input, width: '100%', padding: '8px 12px' }} value={customFrom} onChange={e => setCustomFrom(e.target.value)} max={customTo || undefined} />
                                                </div>
                                                <div>
                                                    <label style={{ ...s.label, fontSize: '10px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '700' }}>END DATE</label>
                                                    <input type="date" style={{ ...s.input, width: '100%', padding: '8px 12px' }} value={customTo} onChange={e => setCustomTo(e.target.value)} min={customFrom || undefined} />
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setShowDatePopover(false)}
                                                style={{ ...s.btnBlue, width: '100%', padding: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}
                                            >
                                                <Check size={14} /> Apply Dates
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ width: '1px', height: '24px', background: '#e2e8f0', margin: '0 8px' }}></div>

                            {/* Filter Popover Button */}
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setShowFilterPopover(!showFilterPopover)}
                                    style={{
                                        ...s.btnGray,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '8px 16px',
                                        background: (filterCity !== 'ALL' || filterArea !== 'ALL' || filterBranch !== 'ALL') ? '#eff6ff' : '#fff',
                                        borderColor: (filterCity !== 'ALL' || filterArea !== 'ALL' || filterBranch !== 'ALL') ? '#3b82f6' : '#e2e8f0',
                                        color: (filterCity !== 'ALL' || filterArea !== 'ALL' || filterBranch !== 'ALL') ? '#2563eb' : '#475569'
                                    }}
                                >
                                    <Filter size={16} />
                                    <span style={{ fontSize: '13px', fontWeight: '700' }}>Region Filters</span>
                                    {(filterCity !== 'ALL' || filterArea !== 'ALL' || filterBranch !== 'ALL') && (
                                        <span style={{ background: '#2563eb', color: '#fff', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '800' }}>!</span>
                                    )}
                                    <ChevronDown size={14} style={{ transform: showFilterPopover ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', marginLeft: '4px' }} />
                                </button>

                                {showFilterPopover && (
                                    <div style={{
                                        position: 'absolute', top: '120%', left: 0,
                                        background: '#fff', borderRadius: '12px', padding: '18px',
                                        boxShadow: '0 15px 40px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0',
                                        zIndex: 100, minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '16px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: '800', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Select Branch & Region</span>
                                            <button onClick={() => setShowFilterPopover(false)} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                                        </div>
                                        <div style={s.hSelectGroup}>
                                            <label style={s.hSelectLabel}>City</label>
                                            <select style={{ ...s.hSelect, padding: '7px 10px' }} value={stagedFilters.city} onChange={e => setStagedFilters({ ...stagedFilters, city: e.target.value })}>
                                                {cities.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div style={s.hSelectGroup}>
                                            <label style={s.hSelectLabel}>Area</label>
                                            <select style={{ ...s.hSelect, padding: '7px 10px' }} value={stagedFilters.area} onChange={e => setStagedFilters({ ...stagedFilters, area: e.target.value })}>
                                                {areas.map(a => <option key={a} value={a}>{a}</option>)}
                                            </select>
                                        </div>
                                        <div style={s.hSelectGroup}>
                                            <label style={s.hSelectLabel}>Branch</label>
                                            <select style={{ ...s.hSelect, padding: '7px 10px' }} value={stagedFilters.branch} onChange={e => setStagedFilters({ ...stagedFilters, branch: e.target.value })}>
                                                {branches.map(b => <option key={b} value={b}>{b}</option>)}
                                            </select>
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                                            <button
                                                onClick={() => {
                                                    const reset = { city: 'ALL', area: 'ALL', branch: 'ALL' };
                                                    setStagedFilters(reset);
                                                    setFilterCity('ALL'); setFilterArea('ALL'); setFilterBranch('ALL');
                                                    setShowFilterPopover(false);
                                                }}
                                                style={{ ...s.btnGray, flex: 1, padding: '8px' }}
                                            >
                                                Clear
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setFilterCity(stagedFilters.city);
                                                    setFilterArea(stagedFilters.area);
                                                    setFilterBranch(stagedFilters.branch);
                                                    setShowFilterPopover(false);
                                                }}
                                                style={{ ...s.btnBlue, flex: 1, padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                            >
                                                <Check size={16} /> Apply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
                            {/* Period label for non-custom */}
                            {activePeriod !== 'custom' && (
                                <div style={{ fontSize: '13px', color: '#1e293b', fontWeight: '700', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    <Calendar size={16} style={{ color: '#3b82f6' }} />
                                    {periodLabel}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div style={{ background: '#fff1f2', border: '1px solid #fda4af', color: '#9f1239', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <AlertCircle size={18} />
                            {error}
                        </div>
                    )}

                    {/* Loading */}
                    {loading && (
                        <div style={{ textAlign: 'center', padding: '80px', color: '#94a3b8', fontSize: '14px' }}>
                            <Loader2 size={40} style={{ animation: 'spin 1.5s linear infinite', margin: '0 auto 12px' }} />
                            <div style={{ fontWeight: '600' }}>Fetching feedback data…</div>
                        </div>
                    )}

                    {!loading && allData.length > 0 && (<>

                        {/* ── KPI Cards ── */}
                        <div style={s.kpiGrid}>
                            {/* NPS Score Card — centered gauge */}
                            <div className="kpi-card" style={{ background: '#fff', borderRadius: '12px', padding: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', gridColumn: 'span 1', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                                <NPSGauge score={npsScore} />
                            </div>
                            {KPI_CARDS.map((k, i) => (
                                <div key={i} className="kpi-card" style={{ ...s.kpiCard, background: k.bg, border: `1px solid ${k.border}` }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ color: k.color, opacity: 0.9 }}>{k.icon}</div>
                                        <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '14px', padding: '2px 6px', fontSize: '9px', fontWeight: '800', color: k.color, letterSpacing: '0.4px' }}>
                                            NPS Score
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '2px' }}>
                                        <div style={{ fontSize: '22px', fontWeight: '800', lineHeight: 1.1, color: k.color }}>{k.num}</div>
                                        <div style={{ fontSize: '11px', color: k.color, opacity: 0.85, fontWeight: '700', marginTop: '2px' }}>{k.lbl}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* ── Rating Breakdown ── */}
                        <div style={s.chartRow}>
                            {CATEGORIES.map(cat => (
                                <RatingBreakdown key={cat.key} data={filtered} categoryKey={cat.key} label={cat.label} icon={cat.icon} />
                            ))}
                        </div>

                        {/* ── Filters / Search ── */}
                        <div style={s.filters}>
                            <div>
                                <label style={s.label}>Search Results</label>
                                <input
                                    style={{ ...s.input, width: '300px' }}
                                    placeholder="Name, Bill No, RefDoc, Mobile…"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                            <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#64748b', alignSelf: 'center', fontWeight: '600' }}>
                                Showing&nbsp;<strong style={{ color: '#1e293b' }}>{filtered.length}</strong>&nbsp;of&nbsp;<strong style={{ color: '#1e293b' }}>{allData.length}</strong>&nbsp;responses
                            </div>
                        </div>

                        {/* ── Table ── */}
                        <div style={s.tbl}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                                    <thead>
                                        <tr>
                                            {['#', 'Ref Doc ID', 'Bill No', 'Business Date', 'City', 'Area', 'Branch', 'Customer', 'Sale Items', 'Ambience', 'Cleanliness', 'Availability', 'Staff', 'Recommend'].map(h => (
                                                <th key={h} style={s.th}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paged.map((r, i) => (
                                            <tr key={r.UniqueID || i}
                                                style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc', transition: 'background 0.15s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                                                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
                                            >
                                                <td style={{ ...s.td, color: '#94a3b8', fontSize: '11px' }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                                                <td style={{ ...s.td, fontWeight: '700', color: '#1d4ed8' }}>{r.RefDocID || '-'}</td>
                                                <td style={{ ...s.td, fontWeight: '600', color: '#1e293b' }}>{r.BillNo || '-'}</td>
                                                <td style={s.td}>{formatDate(r.BusinessDate)}</td>
                                                <td style={s.td}>{r.CityName || r.City || '-'}</td>
                                                <td style={{ ...s.td, color: '#64748b' }}>{r.AreaName || '-'}</td>
                                                <td style={{ ...s.td, fontWeight: '700' }}>{r.BranchName || '-'}</td>
                                                <td style={s.td}>
                                                    <div style={{ fontWeight: '700', color: '#1e293b' }}>{r.UserName || '-'}</div>
                                                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px', fontWeight: '500' }}>{r.Mobile || ''}</div>
                                                </td>
                                                <td style={{ ...s.td, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', color: '#64748b' }} title={r.SaleItems}>{r.SaleItems || '-'}</td>
                                                <td style={s.td}><RatingBadge value={r.AMBIENCE} /></td>
                                                <td style={s.td}><RatingBadge value={r.CLEANLY} /></td>
                                                <td style={s.td}><RatingBadge value={r.AVAILABLE} /></td>
                                                <td style={s.td}><RatingBadge value={r.STAFF} /></td>
                                                <td style={s.td}><RatingBadge value={r.RECOMMEND} /></td>
                                            </tr>
                                        ))}
                                        {paged.length === 0 && (
                                            <tr>
                                                <td colSpan={14} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '60px', fontSize: '14px' }}>
                                                    <Search size={40} style={{ opacity: 0.2, margin: '0 auto 12px' }} />
                                                    No records found for this period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
                                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                                    Page <strong style={{ color: '#1e293b' }}>{page}</strong> of <strong style={{ color: '#1e293b' }}>{totalPages}</strong>
                                </span>
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                    <button style={s.btnGray} disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                        <ChevronLeft size={16} />
                                    </button>
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                                        return p <= totalPages ? (
                                            <button key={p} onClick={() => setPage(p)}
                                                style={{ ...s.btnGray, minWidth: '34px', padding: '6px', background: p === page ? '#2563eb' : '#fff', color: p === page ? '#fff' : '#1e293b', border: '1px solid #e2e8f0', fontWeight: p === page ? '800' : '600' }}
                                            >{p}</button>
                                        ) : null;
                                    })}
                                    <button style={s.btnGray} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>

                    </>)}

                    {!loading && allData.length === 0 && !error && (
                        <div style={{ textAlign: 'center', padding: '100px 20px', color: '#94a3b8' }}>
                            <ClipboardX size={60} style={{ opacity: 0.15, margin: '0 auto 16px' }} />
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>No feedback data available</div>
                            <div style={{ fontSize: '13px' }}>Try refreshing or check your connection</div>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
};

export default FeedbackDashboard;
